import React from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';

import ContextDetail from './ContextDetail';
import ContextId from './ContextId';
import { CAMPAIGN_ASSESSMENTS_URL, CHECKLIST_FLASH_HOTELS_URL } from './constants';
import { formatContextDate } from './utils';

interface Props {
  approvedCount: number;
  bookByDate?: string;
  campaignType?: string;
  channelManager?: string;
  hotelName: string;
  needsApprovalCount: number;
  offerId: string;
  travelToDate?: string;
  totalCount: number;
  onSelectApprovalQueue: () => void;
}

export default function ContextCard({
  approvedCount,
  bookByDate,
  campaignType,
  channelManager,
  hotelName,
  needsApprovalCount,
  offerId,
  travelToDate,
  totalCount,
  onSelectApprovalQueue,
}: Props) {
  const approvedPercent = totalCount > 0 ? Math.round((approvedCount / totalCount) * 100) : 0;
  const contextDetails = [
    { label: 'Travel', value: formatContextDate(travelToDate) },
    { label: 'Book by', value: formatContextDate(bookByDate) },
    { label: 'Channel manager', value: channelManager || 'Not available' },
  ];

  return (
    <Paper sx={{ position: 'relative', overflow: 'hidden', p: { xs: 2, md: 3.5 } }} variant="outlined">
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 4,
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        }}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{ color: 'primary.dark', fontWeight: 700, letterSpacing: 1.2, lineHeight: 1 }}
          >
            Offer context
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
            <Typography variant="h5" component="h2" fontWeight={600}>
              {hotelName}
            </Typography>
            {campaignType && <Chip color="primary" label={campaignType} size="small" />}
          </Stack>
          <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mt: 2 }}>
            <ContextId label="Offer ID" value={offerId} />
          </Stack>
          <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mt: 2 }}>
            {contextDetails.map((detail) => (
              <ContextDetail key={detail.label} label={detail.label} value={detail.value} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.8 }}>
              RULES
            </Typography>
            <Link
              href={CAMPAIGN_ASSESSMENTS_URL}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, fontWeight: 600 }}
            >
              Campaign Assessments <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
            <Link
              href={CHECKLIST_FLASH_HOTELS_URL}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, fontWeight: 600 }}
            >
              Checklist Flash Hotels <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          </Stack>
        </Box>

        <Stack direction="row" spacing={2.25} alignItems="center" sx={{ flexShrink: 0 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress size={104} variant="determinate" value={100} sx={{ color: 'grey.200' }} />
            <CircularProgress
              size={104}
              variant="determinate"
              value={approvedPercent}
              sx={{ position: 'absolute', left: 0, color: 'primary.main' }}
            />
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
              <Typography variant="h6" fontWeight={600}>
                {approvedPercent}%
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.8 }}>
              Approved
            </Typography>
            <Typography variant="h4" fontWeight={600} sx={{ lineHeight: 1.1 }}>
              {approvedCount}
              <Box component="span" sx={{ color: 'grey.400', fontWeight: 400 }}>
                /{totalCount}
              </Box>
            </Typography>
            <Typography
              variant="caption"
              color="warning.dark"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}
            >
              <Button
                color="warning"
                size="small"
                startIcon={<WarningAmberIcon sx={{ fontSize: 15 }} />}
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  minWidth: 0,
                  p: 0,
                  textTransform: 'none',
                  verticalAlign: 'baseline',
                }}
                variant="text"
                onClick={onSelectApprovalQueue}
              >
                {needsApprovalCount} items need review
              </Button>
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
